// Global Instructions Rule Applied!
//
// LineDiff — renders lineDiff ops for the minutes History modal: added lines
// on a green tint, removed lines struck through on a red tint, unchanged
// lines muted. Monospace is deliberate: markdown source, not rendered prose.
// Wide lines scroll inside the box (design patterns: never the page).
import React, { useMemo } from 'react'
import { diffLines, diffSummary } from '../utils/lineDiff.ts'

interface LineDiffProps {
  before: string
  after: string
  /** Shown above the lines (e.g. "vs version 2"). */
  caption?: string
  maxHeight?: number
}

const ROW_CLASS: Record<string, string> = {
  same: 'text-gray-500',
  add: 'bg-green-50 text-green-900',
  del: 'bg-red-50 text-gray-500 line-through'
}

const MARK: Record<string, string> = { same: ' ', add: '+', del: '−' }

export const LineDiff: React.FC<LineDiffProps> = ({ before, after, caption, maxHeight = 420 }) => {
  const ops = useMemo(() => diffLines(before, after), [before, after])
  const summary = useMemo(() => diffSummary(ops), [ops])
  return (
    <div data-component='LineDiff' className='text-[12px]'>
      <div className='flex items-baseline justify-between text-[11.5px] text-gray-500 mb-1'>
        <span>{caption}</span>
        <span>
          <span className='text-green-700'>+{summary.added}</span>
          {' · '}
          <span className='text-red-700'>−{summary.removed}</span>
        </span>
      </div>
      <div className='border border-gray-200 rounded overflow-auto font-mono' style={{ maxHeight }}>
        {ops.length === 0 ? (
          <div className='text-gray-400 px-2 py-1'>(empty)</div>
        ) : (
          ops.map((op, index) => (
            <div key={index} className={`flex whitespace-pre px-2 leading-5 ${ROW_CLASS[op.kind]}`}>
              <span className='w-4 flex-shrink-0 select-none text-gray-400'>{MARK[op.kind]}</span>
              <span>{op.text || ' '}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
